import { useEffect, useMemo, useState } from "react";
import { Login } from "./components/Login";
import { Chat } from "./components/Chat";
import { Settings } from "./components/Settings";
import { ConversationList } from "./components/ConversationList";
import { ActionLog } from "./components/ActionLog";
import { loadConfig, saveConfig } from "./lib/storage";
import { whoami } from "./lib/api";
import { AGENTS } from "./lib/agents";
import type { AgentId, AppConfig } from "./types";

type Tab = "chat" | "history" | "settings" | "audit";

export function App() {
  const [tab, setTab] = useState<Tab>("chat");
  const [config, setConfig] = useState<AppConfig>(() => loadConfig());
  const [agentId, setAgentId] = useState<AgentId>("comercial");
  const [convId, setConvId] = useState<string | null>(null);
  const [authedEmail, setAuthedEmail] = useState<string | null>(null);
  const [authChecking, setAuthChecking] = useState(true);

  useEffect(() => {
    saveConfig(config);
  }, [config]);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (!config.apiKey) {
        setAuthedEmail(null);
        setAuthChecking(false);
        return;
      }
      try {
        const me = await whoami(config);
        if (!cancelled) setAuthedEmail(me.email);
      } catch {
        if (!cancelled) setAuthedEmail(null);
      } finally {
        if (!cancelled) setAuthChecking(false);
      }
    }
    check();
    return () => {
      cancelled = true;
    };
  }, [config.apiUrl, config.apiKey]);

  const agent = useMemo(() => AGENTS.find((a) => a.id === agentId)!, [agentId]);

  if (authChecking) {
    return <div className="loading">carregando…</div>;
  }

  if (!authedEmail) {
    return (
      <Login
        config={config}
        onLogin={(token, email) => {
          const next = { ...config, apiKey: token };
          setConfig(next);
          setAuthedEmail(email);
        }}
        onConfigUrl={(u) => setConfig({ ...config, apiUrl: u })}
      />
    );
  }

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <span className="dot" /> SupriAI Launcher
          <span className="badge">{authedEmail}</span>
        </div>
        <nav className="tabs">
          <button className={tab === "chat" ? "on" : ""} onClick={() => setTab("chat")}>
            Chat
          </button>
          <button className={tab === "history" ? "on" : ""} onClick={() => setTab("history")}>
            Conversas
          </button>
          <button className={tab === "audit" ? "on" : ""} onClick={() => setTab("audit")}>
            Auditoria
          </button>
          <button className={tab === "settings" ? "on" : ""} onClick={() => setTab("settings")}>
            Config
          </button>
          <button
            className="logout"
            onClick={() => {
              setConfig({ ...config, apiKey: "" });
              setAuthedEmail(null);
            }}
          >
            sair
          </button>
        </nav>
      </header>

      <main className="main">
        {tab === "chat" && (
          <Chat
            config={config}
            agentId={agentId}
            setAgentId={setAgentId}
            convId={convId}
            setConvId={setConvId}
          />
        )}
        {tab === "history" && (
          <ConversationList
            config={config}
            onOpen={(id) => {
              setConvId(id);
              setTab("chat");
            }}
            current={convId}
          />
        )}
        {tab === "audit" && <ActionLog config={config} />}
        {tab === "settings" && <Settings config={config} setConfig={setConfig} />}
      </main>

      <footer className="footer">
        <span>
          Agente ativo: <b>{agent.nome}</b>
          {convId && <> · Conversa: <code>{convId.slice(0, 8)}</code></>}
        </span>
        <span>SupriAI v0.1 — CRM Suprimentos</span>
      </footer>
    </div>
  );
}
